import type { Ctx } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { AoSGameState } from '../../types';
import { coordToKey } from '../../types';
import { updateBuildCostInfo } from './passBuildTrack';

/**
 * 全ての線路建設をキャンセル
 *
 * このフェーズ中に配置したすべてのタイルを撤去します。
 * コストはまだ支払われていないため返還処理は不要です。
 *
 * - 新都市タイルは元の町ヘクスに戻します
 * - タイル置き換えの場合は元のタイルに戻します
 * - 新規配置の場合はタイルを削除します
 * - 特殊リンクの所有権をリセットします
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @returns void
 */
export function cancelAllBuildTrack(
  { G, ctx }: { G: AoSGameState; ctx: Ctx }
): void {
  const player = G.players[ctx.currentPlayer];

  // 履歴がなければ何もしない
  if (!player.buildTrackHistory || player.buildTrackHistory.length === 0) {
    debug.log('[cancelAllBuildTrack] No history to undo');
    return;
  }

  debug.log(`[cancelAllBuildTrack] Undoing ${player.buildTrackHistory.length} placements`);

  // 履歴を逆順に処理（最後に配置したものから順に巻き戻す）
  const reversedHistory = [...player.buildTrackHistory].reverse();

  for (const entry of reversedHistory) {
    // 特殊リンクの場合
    if (entry.isSpecialLink && entry.specialLinkId) {
      // 所有権をリセット（スナップショットから復元されるので個別処理は不要だが、念のため）
      debug.log(`[cancelAllBuildTrack] Undoing special link: ${entry.specialLinkType} ${entry.specialLinkId}`);

      // タイル配置カウントを減らす
      player.tilesPlacedThisTurn = Math.max(0, player.tilesPlacedThisTurn - 1);
      continue;
    }

    // 通常タイルの場合
    const hexKey = coordToKey(entry.hexCoordinate);
    const hex = G.map.find(h => coordToKey(h.coordinate) === hexKey);

    if (!hex) {
      console.error(`[cancelAllBuildTrack] Hex not found: ${hexKey}`);
      continue;
    }

    // コスト返還は不要（まだ支払っていないため）
    // player.cash += entry.cost;

    if (entry.isNewCityTile) {
      // 新都市タイル: 元の町の状態に戻す
      if (hex.town && entry.previousTownState) {
        hex.town.isUrbanized = entry.previousTownState.isUrbanized;
        hex.town.newCity = entry.previousTownState.newCity;
        debug.log(`[cancelAllBuildTrack] Restored town state at ${hexKey}`);
      }

      // 新都市タイルを削除し、元のタイルがあれば復元
      if (entry.previousTile) {
        hex.tile = entry.previousTile;
        debug.log(`[cancelAllBuildTrack] Restored previous tile ${entry.previousTile.type} at ${hexKey}`);
      } else {
        hex.tile = null;
        debug.log(`[cancelAllBuildTrack] Removed new city tile at ${hexKey}`);
      }

      // 新都市タイル配置カウントを減らす
      player.newCityTilesPlacedThisTurn = Math.max(0, (player.newCityTilesPlacedThisTurn ?? 1) - 1);
    } else if (entry.placementType === 'replace') {
      // 置き換え（単線→複線、単線→単線、複線→複線すべて含む）: 元のタイルに戻す
      if (entry.previousTile) {
        hex.tile = entry.previousTile;
        debug.log(`[cancelAllBuildTrack] Restored replaced tile ${entry.previousTile.type} at ${hexKey}`);
      } else {
        hex.tile = null;
        debug.log(`[cancelAllBuildTrack] Removed replacement tile at ${hexKey} (no previous tile)`);
      }

      // タイル配置カウントを減らす
      player.tilesPlacedThisTurn = Math.max(0, player.tilesPlacedThisTurn - 1);
    } else {
      // 新規配置: タイルを削除
      hex.tile = null;
      debug.log(`[cancelAllBuildTrack] Removed tile at ${hexKey}`);

      // タイル配置カウントを減らす
      player.tilesPlacedThisTurn = Math.max(0, player.tilesPlacedThisTurn - 1);
    }

    // Town Disk 使用をリセット
    if (entry.townDiskUsed && entry.townId) {
      if (G.townDiskPlaced) {
        G.townDiskPlaced[entry.townId] = false;
      }
      if (G.townDisksRemaining !== undefined) {
        G.townDisksRemaining += 1;
      }
      debug.log(`[cancelAllBuildTrack] Town Disk returned for town ${entry.townId}. Remaining: ${G.townDisksRemaining}`);
    }
  }

  // Issue #22: スナップショットから completedLinks と unfinishedSections を復元
  if (G.buildTrackPhaseSnapshot) {
    G.completedLinks = JSON.parse(JSON.stringify(G.buildTrackPhaseSnapshot.completedLinks));
    G.unfinishedSections = JSON.parse(JSON.stringify(G.buildTrackPhaseSnapshot.unfinishedSections));
    debug.log(`[cancelAllBuildTrack] Restored from snapshot: ${G.completedLinks.length} links, ${G.unfinishedSections.length} sections`);

    // 特殊リンクの所有権を復元
    if (G.buildTrackPhaseSnapshot.ferryLinkOwners && G.ferryLinks) {
      for (const saved of G.buildTrackPhaseSnapshot.ferryLinkOwners) {
        const link = G.ferryLinks.find(l => l.id === saved.id);
        if (link) {
          link.owner = saved.owner;
        }
      }
      debug.log(`[cancelAllBuildTrack] Restored ferry link owners`);
    }

    if (G.buildTrackPhaseSnapshot.adjacentLinkOwners && G.adjacentLinks) {
      for (const saved of G.buildTrackPhaseSnapshot.adjacentLinkOwners) {
        const link = G.adjacentLinks.find(l => l.id === saved.id);
        if (link) {
          link.owner = saved.owner;
        }
      }
      debug.log(`[cancelAllBuildTrack] Restored adjacent link owners`);
    }
  }

  // 履歴をクリア
  player.buildTrackHistory = [];
  debug.log('[cancelAllBuildTrack] History cleared');

  // 建設コスト情報を更新（UI表示用）
  updateBuildCostInfo(G, ctx.currentPlayer);
}
